import React, { useMemo } from "react";

import { useLookup, useSearch, useUrl } from "@ofqual/portal-core/core/hooks";
import {
  FilterMenu,
  FilterSearch,
} from "@ofqual/portal-components/components/ResponseFilter";
import { FilterList } from "@material-ui/icons";
import { Select } from "@ofqual/portal-components/components/Select";
import { makeStyles, MenuItem, Theme } from "@material-ui/core";
import {
  aoService,
  aoServiceSearch,
  confidencesService,
  riskTypesService,
  systemicSourcesService,
  valencesService,
} from "../services";
import { SearchField } from "@ofqual/portal-components/components/SearchField";
import { useSelector } from "react-redux";
import { authSelector } from "../redux/selectors";
import { QueryParams } from "@ofqual/portal-core/core/types";
import { ensureParamIsArray } from "@ofqual/portal-core/core/utils/urlHelper";

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      "& fieldset": {
        borderColor: theme.palette.primary.main,
      },
    },
    menuItem: {
      "&:hover": {
        backgroundColor: "transparent",
      },
    },
  };
});

export const RisksFilterControls: React.FC = () => {
  const classes = useStyles();
  const {
    searchParams,
    navigateToNextSearch,
    searchParamsFiltersOnly,
  } = useSearch(useUrl());


  const [
    systemicSourcesLookups,
    valenceLookups,
    riskTypesLookups,
    confidenceLookups,
  ] = useLookup(
    {
      service: systemicSourcesService,
      id: "id",
      label: "tag",
    },
    {
      service: valencesService,
      id: "id",
      label: "valence",
    },
    {
      service: riskTypesService,
      id: "id",
      label: "riskTypeName",
    },
    {
      service: confidencesService,
      id: "id",
      label: "confidence",
    }
  );

  const { token } = useSelector(authSelector);

  const mappedOrgLookupService = useMemo(
    () => ({
      get: (id: string) => aoService.get(id.toString(), { token }),
      search: (params?: QueryParams) =>
        aoServiceSearch.search(params, { token }),
    }),
    [token]
  );

  return (
    <>
      <FilterSearch
        title="Search..."
        value={searchParams.search}
        onChange={(value) =>
          navigateToNextSearch({
            search: value,
          })
        }
        dataTest={"svr-search"}
      />
      &nbsp;&nbsp;
      <FilterMenu
        Icon={FilterList}
        title="Filters"
        dataTest="svr-filter-menu"
        search={{ searchParamsFiltersOnly, navigateToNextSearch }}
      >
        {(workingParams, dirtyParams, addParam) => (
          <form>
            <MenuItem className={classes.menuItem}>
              <SearchField
                searchFieldVariant="multiple"
                className={
                  dirtyParams.includes("submissionOrganisations")
                    ? classes.root
                    : undefined
                }
                onChange={(e) =>
                  addParam("submissionOrganisations", e.target.value)
                }
                value={workingParams.submissionOrganisations}
                label="Awarding Organisations"
                datatest="filter-svr-aos"
                service={mappedOrgLookupService}
              />
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                selectVariant="multiple"
                className={
                  dirtyParams.includes("submissionSources")
                    ? classes.root
                    : undefined
                }
                label="Source(s)"
                data-test="svr-sources-filter"
                value={ensureParamIsArray(workingParams.submissionSources)}
                onChange={(e) => {
                  addParam("submissionSources", e.target.value);
                }}
              >
                {systemicSourcesLookups.map(x => ({id: x.id.toString(), label: x.label}))}
              </Select>
            </MenuItem>

            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("valenceId") ? classes.root : undefined
                }
                label="Flag or Mitigation"
                data-test="svr-valence-filter"
                value={workingParams.valenceId}
                onChange={(e) => addParam("valenceId", e.target.value)}
              >
                {valenceLookups}
              </Select>
            </MenuItem>
            <MenuItem className={classes.menuItem}>
              <Select
                selectVariant="multiple"
                className={
                  dirtyParams.includes("submissionRiskTypes")
                    ? classes.root
                    : undefined
                }
                label="Risk Type(s)"
                data-test="svr-risk-type-filter"
                value={ensureParamIsArray(workingParams.submissionRiskTypes)}
                onChange={(e) =>
                  addParam("submissionRiskTypes", e.target.value)
                }
              >
                {riskTypesLookups.map(x => ({id: x.id.toString(), label: x.label}))}
              </Select>
            </MenuItem>

            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("confidenceId")
                    ? classes.root
                    : undefined
                }
                label="Confidence Level"
                data-test="svr-confidence-filter"
                value={workingParams.confidenceId}
                onChange={(e) => addParam("confidenceId", e.target.value)}
              >
                {confidenceLookups}
              </Select>
            </MenuItem>

            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("level") ? classes.root : undefined
                }
                label="Type"
                data-test="svr-type-filter"
                value={workingParams.submissionType}
                onChange={(e) => addParam("submissionType", e.target.value)}
              >
                {[
                  { id: "0", label: "Awarding Organisation" },
                  { id: "1", label: "Systemic" },
                ]}
              </Select>
            </MenuItem>

            <MenuItem className={classes.menuItem}>
              <Select
                className={
                  dirtyParams.includes("active") ? classes.root : undefined
                }
                label="Status"
                data-test="svr-status-filter"
                value={workingParams.active}
                onChange={(e) => addParam("active", e.target.value)}
              >
                {[
                  { id: "true", label: "Active" },
                  { id: "false", label: "Deactive" },
                ]}
              </Select>
            </MenuItem>
          </form>
        )}
      </FilterMenu>
    </>
  );
};
